"use client";

import { useRef, useState, useEffect } from "react";
import Image from "@/components/ui/image";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Link,
  Palette,
  LayoutDashboard,
  Plug,
  GitBranch,
  Inbox,
  CreditCard,
  X,
  Check,
  Phone,
  Calendar,
  Star,
  MessageSquare,
  Headphones,
  AlertTriangle,
  LifeBuoy,
  FileText,
  History,
  BarChart3,
  Settings,
  Play,
  ChevronRight,
} from "lucide-react";
import ayraLogo from "@/assets/ayra-logo.png";
import vapiLogo from "@/assets/vapi-logo.png";
import retellLogo from "@/assets/retell-logo.jpeg";
import ultravoxLogo from "@/assets/ultravox-logo.jpeg";
import googleCalendarLogo from "@/assets/google-calendar-logo.png";
import jobberLogo from "@/assets/jobber-logo.png";
import opentableLogo from "@/assets/opentable-logo.png";
import ghlLogo from "@/assets/ghl-logo.png";
import housecallProLogo from "@/assets/housecall-pro-logo.webp";
import mindbodyLogo from "@/assets/mindbody-logo.png";
import hubspotLogo from "@/assets/hubspot-logo.png";

const features = [
  {
    id: "connect",
    icon: Link,
    title: "Simply connect your Vapi or Retell agent.",
    subtitle: "Launch in one day. Not one month. One day.",
  },
  {
    id: "whitelabel",
    icon: Palette,
    title: "Your logo. Your colors. Your domain.",
    subtitle:
      "A complete AI platform under your brand. Clients never see our name.",
  },
  {
    id: "dashboard",
    icon: LayoutDashboard,
    title: "Your clients see a polished product.",
    subtitle:
      "Calls. Transcripts. Recordings. Everything in one place. You control everything under the hood.",
  },
  {
    id: "integrations",
    icon: Plug,
    title: "Integrations are the hard part. We solved it.",
    subtitle:
      "GoHighLevel. HubSpot. Google Calendar. Salesforce. One click to connect. No webhook nightmares.",
  },
  {
    id: "workflows",
    icon: GitBranch,
    title: "Workflows built for AI.",
    subtitle:
      "Confirmations. Follow-ups. Handoffs. Pre-built and ready. Or drag-and-drop your own. No Zapier. No per-task fees.",
  },
  {
    id: "omnichannel",
    icon: Inbox,
    title: "Voice. Text. Email. DMs.",
    subtitle:
      "One inbox for your clients. All channels. Nothing falls through.",
  },
  {
    id: "billing",
    icon: CreditCard,
    title: "Set your tiers. Set your margins.",
    subtitle:
      "Bill per call. Per minute. Per month. Billing runs itself. No chasing clients.",
  },
];

const platformOptions = [
  {
    id: "ayra",
    name: "Ayra Native",
    description: "Recommended for maximum flexibility",
    logo: ayraLogo,
    recommended: true,
  },
  {
    id: "vapi",
    name: "Vapi",
    description: "Voice-first experiences",
    logo: vapiLogo,
    recommended: false,
  },
  {
    id: "retell",
    name: "Retell",
    description: "Optimized for customer support",
    logo: retellLogo,
    recommended: false,
  },
  {
    id: "ultravox",
    name: "Ultravox",
    description: "High-performance conversational AI",
    logo: ultravoxLogo,
    recommended: false,
  },
];

const ConnectAgentVisual = () => {
  const [selectedPlatform, setSelectedPlatform] = useState("ayra");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative w-full max-w-[380px] sm:max-w-[420px] md:max-w-[480px] mx-auto"
    >
      {/* Modal container */}
      <div className="bg-dark-surface rounded-lg md:rounded-2xl border border-dark-border shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 md:px-6 md:py-5 border-b border-dark-border">
          <motion.h3 
            className="text-xs md:text-lg font-semibold text-dark-text"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Create New Agent
          </motion.h3>
          <button className="w-5 h-5 md:w-8 md:h-8 rounded-lg flex items-center justify-center text-dark-muted hover:text-dark-text hover:bg-dark-muted/10 transition-colors">
            <X className="w-3 h-3 md:w-5 md:h-5" />
          </button>
        </div>

        {/* Progress steps */}
        <div className="px-3 py-2 md:px-6 md:py-4">
          <div className="flex items-center gap-1 md:gap-2">
            <div className="flex-1 h-0.5 md:h-1 rounded-full bg-dark-text" />
            <div className="flex-1 h-0.5 md:h-1 rounded-full bg-dark-border" />
            <div className="flex-1 h-0.5 md:h-1 rounded-full bg-dark-border" />
            <div className="flex-1 h-0.5 md:h-1 rounded-full bg-dark-border" />
          </div>
        </div>

        {/* Content */}
        <div className="px-3 pb-3 md:px-6 md:pb-6">
          {/* Section title */}
          <div className="mb-3 md:mb-5">
            <motion.h4 
              className="text-[10px] md:text-base font-semibold text-dark-text mb-0.5 md:mb-1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Choose Platform
            </motion.h4>
            <motion.p 
              className="text-[9px] md:text-sm text-dark-muted"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Select where your agent will operate.
            </motion.p>
          </div>

          {/* Platform cards grid */}
          <div className="grid grid-cols-2 gap-2 md:gap-3">
            {platformOptions.map((platform, index) => (
              <motion.button
                key={platform.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05, duration: 0.3 }}
                onClick={() => setSelectedPlatform(platform.id)}
                className={`relative flex items-start gap-1.5 md:gap-3 p-2 md:p-4 rounded-md md:rounded-xl border transition-all duration-200 text-left ${
                  selectedPlatform === platform.id
                    ? "bg-dark-muted/10 border-dark-text/30"
                    : "bg-transparent border-dark-border hover:border-dark-muted/50 hover:bg-dark-muted/5"
                }`}
              >
                {/* Selection indicator */}
                {selectedPlatform === platform.id && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-1.5 right-1.5 md:top-2.5 md:right-2.5 w-3.5 h-3.5 md:w-5 md:h-5 rounded-full bg-primary flex items-center justify-center"
                  >
                    <Check className="w-2 h-2 md:w-3 md:h-3 text-white" />
                  </motion.div>
                )}

                {/* Logo */}
                <div className="w-5 h-5 md:w-10 md:h-10 rounded-md md:rounded-lg overflow-hidden flex-shrink-0 bg-white flex items-center justify-center">
                  <Image
                    src={platform.logo}
                    alt={platform.name}
                    width={32}
                    height={32}
                    className="w-4 h-4 md:w-8 md:h-8 object-contain"
                  />
                </div>

                {/* Text content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-0.5 md:gap-2 mb-0.5">
                    <span className="text-[9px] md:text-sm font-medium text-dark-text">
                      {platform.name}
                    </span>
                    {platform.recommended && (
                      <span className="px-0.5 py-0.5 md:px-1.5 md:py-0.5 text-[7px] md:text-[10px] font-medium bg-dark-text text-dark rounded md:rounded-md">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="text-[8px] md:text-xs text-dark-muted leading-relaxed">
                    {platform.description}
                  </p>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-3 py-2 md:px-6 md:py-4 border-t border-dark-border">
          <button className="px-2 py-1 md:px-4 md:py-2 text-[9px] md:text-sm font-medium text-dark-text bg-dark-muted/10 hover:bg-dark-muted/20 rounded-md md:rounded-lg transition-colors">
            Cancel
          </button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-2 py-1 md:px-5 md:py-2 text-[9px] md:text-sm font-medium bg-dark-text text-dark rounded-md md:rounded-lg hover:bg-dark-text/90 transition-colors"
          >
            Continue
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

const FeatureVisual = ({ featureId }: { featureId: string }) => {
  switch (featureId) {
    case "connect":
      return (
        <div className="relative w-full h-full flex items-center justify-center p-4">
          {/* Canvas background with subtle grid dots */}
          <div className="absolute inset-0 opacity-20">
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(circle, hsl(var(--dark-muted)) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            />
          </div>
          <div className="relative z-10">
            <ConnectAgentVisual />
          </div>
        </div>
      );
    case "whitelabel":
      return (
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Canvas background with subtle grid dots */}
          <div className="absolute inset-0 opacity-20">
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(circle, hsl(var(--dark-muted)) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            />
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative z-10 bg-dark-surface rounded-xl md:rounded-2xl p-4 md:p-6 w-[300px] sm:w-72 md:w-80 border border-dark-border"
          >
            <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
              <motion.div
                animate={{
                  backgroundColor: ["#5F8F84", "#3B82F6", "#F59E0B", "#5F8F84"],
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="w-8 h-8 md:w-10 md:h-10 rounded-lg"
              />
              <motion.div
                animate={{ width: ["60%", "80%", "70%", "60%"] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="h-3 md:h-4 bg-dark-muted/30 rounded"
              />
            </div>
            <div className="text-[10px] md:text-xs text-dark-muted mb-2">
              yourbrand.ayra.ai
            </div>
            <div className="space-y-1.5 md:space-y-2">
              <div className="h-2.5 md:h-3 bg-dark-muted/20 rounded w-full" />
              <div className="h-2.5 md:h-3 bg-dark-muted/20 rounded w-3/4" />
            </div>
          </motion.div>
        </div>
      );
    case "dashboard":
      return (
        <div className="relative w-full h-full flex items-center justify-center p-2">
          {/* Canvas background with subtle grid dots */}
          <div className="absolute inset-0 opacity-20">
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(circle, hsl(var(--dark-muted)) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 bg-dark-surface rounded-xl md:rounded-2xl border border-dark-border shadow-2xl overflow-hidden w-full max-w-[420px] sm:max-w-[480px] md:max-w-[640px]"
            style={{ boxShadow: "0 0 60px rgba(0,0,0,0.4)" }}
          >
            <div className="flex h-[360px] sm:h-[400px] md:h-[420px]">
              {/* Sidebar Navigation */}
              <div className="w-[110px] sm:w-[120px] md:w-[130px] bg-[hsl(216_28%_6%)] border-r border-dark-border flex flex-col py-2 px-1.5 md:py-2 md:px-1.5">
                {/* Logo */}
                <div className="flex items-center gap-1.5 px-2 mb-3 md:mb-3">
                  <div className="w-5 h-5 md:w-5 md:h-5 rounded-md bg-primary flex items-center justify-center">
                    <span className="text-[8px] md:text-[9px] font-bold text-white">
                      A
                    </span>
                  </div>
                  <span className="text-[10px] md:text-[11px] font-semibold text-dark-text">
                    Ayra
                  </span>
                </div>

                {/* Nav Items */}
                <nav className="flex-1 space-y-0.5 overflow-y-auto">
                  {[
                    { icon: LayoutDashboard, label: "Dashboard", active: true },
                    { icon: AlertTriangle, label: "Escalations", badge: 5 },
                    { icon: History, label: "Call History" },
                    { icon: Inbox, label: "Inbox", badge: 3 },
                    { icon: BarChart3, label: "Analytics" },
                    { icon: LifeBuoy, label: "Support" },
                    { icon: Settings, label: "Settings" },
                  ].map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.02 }}
                      className={`flex items-center gap-1.5 px-2 py-1.5 md:py-1 rounded-md text-[9px] md:text-[10px] cursor-pointer transition-colors ${
                        item.active
                          ? "bg-dark-muted/15 text-dark-text"
                          : "text-dark-muted hover:text-dark-text hover:bg-dark-muted/10"
                      }`}
                    >
                      <item.icon className="w-3 h-3 md:w-3 md:h-3 flex-shrink-0" />
                      <span className="flex-1 truncate">
                        {item.label}
                      </span>
                      {item.badge && (
                        <span className="w-3.5 h-3.5 md:w-3.5 md:h-3.5 rounded-full bg-primary text-[8px] md:text-[8px] font-medium text-white flex items-center justify-center">
                          {item.badge}
                        </span>
                      )}
                    </motion.div>
                  ))}
                </nav>
              </div>

              {/* Main Content */}
              <div className="flex-1 flex flex-col overflow-hidden bg-[hsl(216_28%_8%)]">
                {/* Header */}
                <div className="flex items-center justify-between px-3 md:px-3 py-2.5 md:py-2 border-b border-dark-border">
                  <div>
                    <h4 className="text-[10px] md:text-[11px] font-semibold text-dark-text">
                      Welcome back, DentalCo
                    </h4>
                    <p className="text-[8px] md:text-[9px] text-dark-muted">
                      AI agent performance
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 md:gap-1.5">
                    <div className="w-5 h-5 md:w-5 md:h-5 rounded-full bg-dark-muted/20 flex items-center justify-center">
                      <span className="text-[8px] md:text-[9px] text-dark-muted">
                        🔔
                      </span>
                    </div>
                    <div className="w-5 h-5 md:w-5 md:h-5 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-[8px] md:text-[9px] font-medium text-primary">
                        D
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-2.5 md:p-2.5 space-y-2 md:space-y-2">
                  {/* Metric Cards */}
                  <div className="grid grid-cols-4 gap-2 md:gap-1.5">
                    {[
                      {
                        icon: Phone,
                        label: "Calls",
                        value: "24",
                        trend: "+50%",
                        color: "blue",
                      },
                      {
                        icon: Calendar,
                        label: "Booked",
                        value: "8",
                        trend: "+33%",
                        color: "green",
                      },
                      {
                        icon: Star,
                        label: "Rating",
                        value: "4.8",
                        color: "amber",
                      },
                      {
                        icon: MessageSquare,
                        label: "Messages",
                        value: "4",
                        color: "purple",
                      },
                    ].map((metric, i) => (
                      <motion.div
                        key={metric.label}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 + i * 0.03 }}
                        className="bg-dark-surface rounded-lg md:rounded-lg p-2 md:p-2 border border-dark-border"
                      >
                        <div className="flex items-center gap-1.5 md:gap-1.5 mb-1 md:mb-1">
                          <div
                            className={`w-4 h-4 md:w-4 md:h-4 rounded flex items-center justify-center ${
                              metric.color === "blue"
                                ? "bg-blue-500/15"
                                : metric.color === "green"
                                ? "bg-green-500/15"
                                : metric.color === "amber"
                                ? "bg-amber-500/15"
                                : "bg-purple-500/15"
                            }`}
                          >
                            <metric.icon
                              className={`w-2.5 h-2.5 md:w-2.5 md:h-2.5 ${
                                metric.color === "blue"
                                  ? "text-blue-400"
                                  : metric.color === "green"
                                  ? "text-green-400"
                                  : metric.color === "amber"
                                  ? "text-amber-400"
                                  : "text-purple-400"
                              }`}
                            />
                          </div>
                          <span className="text-[8px] md:text-[8px] text-dark-muted">
                            {metric.label}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1 md:gap-1">
                          <span className="text-sm md:text-sm font-bold text-dark-text">
                            {metric.value}
                          </span>
                          {metric.trend && (
                            <span className="text-[8px] md:text-[8px] text-primary">
                              {metric.trend}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-1.5 md:gap-1.5">
                    {[
                      { icon: Headphones, label: "Listen" },
                      { icon: AlertTriangle, label: "Report" },
                      { icon: LifeBuoy, label: "Support" },
                      { icon: FileText, label: "Reports" },
                    ].map((action, i) => (
                      <motion.button
                        key={action.label}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 + i * 0.02 }}
                        className="flex-1 flex items-center justify-center gap-1 md:gap-1 py-1.5 md:py-1.5 rounded-lg md:rounded-lg bg-dark-surface border border-dark-border hover:border-dark-muted/50 transition-colors"
                      >
                        <action.icon className="w-3 h-3 md:w-3 md:h-3 text-dark-muted" />
                        <span className="text-[8px] md:text-[8px] text-dark-muted">
                          {action.label}
                        </span>
                      </motion.button>
                    ))}
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-5 gap-2 md:gap-1.5">
                    {/* Call Volume Chart */}
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="col-span-3 bg-dark-surface rounded-lg md:rounded-lg p-2 md:p-2 border border-dark-border"
                    >
                      <h5 className="text-[9px] md:text-[9px] font-medium text-dark-text mb-1.5 md:mb-1.5">
                        Call Volume
                      </h5>
                      <div className="flex items-end gap-0.5 h-10 md:h-10">
                        {[16, 14, 18, 24, 22, 12, 8].map((h, i) => (
                          <motion.div
                            key={i}
                            initial={{ height: 0 }}
                            animate={{ height: `${h * 1.5}px` }}
                            transition={{
                              delay: 0.35 + i * 0.03,
                              duration: 0.3,
                            }}
                            className="flex-1 bg-gradient-to-t from-primary/60 to-primary rounded-sm"
                          />
                        ))}
                      </div>
                      <div className="flex justify-between mt-1 md:mt-1">
                        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                          <span
                            key={i}
                            className="text-[7px] md:text-[7px] text-dark-muted flex-1 text-center"
                          >
                            {d}
                          </span>
                        ))}
                      </div>
                    </motion.div>

                    {/* Call Outcomes */}
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.35 }}
                      className="col-span-2 bg-dark-surface rounded-lg md:rounded-lg p-2 md:p-2 border border-dark-border"
                    >
                      <h5 className="text-[9px] md:text-[9px] font-medium text-dark-text mb-1">
                        Outcomes
                      </h5>
                      <div className="flex items-center gap-1.5 md:gap-1.5">
                        <div className="relative w-8 h-8 md:w-8 md:h-8">
                          <svg
                            className="w-8 h-8 md:w-8 md:h-8 -rotate-90"
                            viewBox="0 0 36 36"
                          >
                            <circle
                              cx="18"
                              cy="18"
                              r="14"
                              fill="none"
                              stroke="hsl(var(--dark-border))"
                              strokeWidth="4"
                            />
                            <motion.circle
                              cx="18"
                              cy="18"
                              r="14"
                              fill="none"
                              stroke="hsl(var(--primary))"
                              strokeWidth="4"
                              strokeDasharray="88"
                              initial={{ strokeDashoffset: 88 }}
                              animate={{ strokeDashoffset: 88 - 88 * 0.45 }}
                              transition={{ delay: 0.4, duration: 0.4 }}
                            />
                          </svg>
                        </div>
                        <div className="space-y-0.5 text-[7px] md:text-[7px]">
                          <div className="flex items-center gap-1 md:gap-1">
                            <div className="w-1 h-1 rounded-full bg-primary" />
                            <span className="text-dark-muted">Booked 45%</span>
                          </div>
                          <div className="flex items-center gap-1 md:gap-1">
                            <div className="w-1 h-1 rounded-full bg-blue-500" />
                            <span className="text-dark-muted">Info 30%</span>
                          </div>
                          <div className="flex items-center gap-1 md:gap-1">
                            <div className="w-1 h-1 rounded-full bg-dark-muted/40" />
                            <span className="text-dark-muted">Other 25%</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>

                  {/* Recent Calls */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-dark-surface rounded-lg md:rounded-lg p-2.5 md:p-2.5 border border-dark-border"
                  >
                    <div className="flex items-center justify-between mb-2 md:mb-2">
                      <h5 className="text-[10px] md:text-[10px] font-medium text-dark-text">
                        Recent Calls
                      </h5>
                      <div className="flex gap-0.5">
                        {["Calls", "Transcripts", "Recordings"].map(
                          (tab, i) => (
                            <span
                              key={tab}
                              className={`text-[7px] md:text-[7px] px-1.5 md:px-1.5 py-0.5 rounded ${
                                i === 0
                                  ? "bg-primary/20 text-primary"
                                  : "text-dark-muted hover:text-dark-text cursor-pointer"
                              }`}
                            >
                              {tab}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                    <div className="space-y-0">
                      {[
                        {
                          time: "2:45 PM",
                          name: "John Smith",
                          outcome: "Booked",
                          duration: "4:32",
                          color: "green",
                        },
                        {
                          time: "2:18 PM",
                          name: "Sarah Johnson",
                          outcome: "Info Provided",
                          duration: "2:15",
                          color: "blue",
                        },
                        {
                          time: "1:55 PM",
                          name: "Mike Davis",
                          outcome: "Booked",
                          duration: "3:47",
                          color: "green",
                        },
                        {
                          time: "1:32 PM",
                          name: "Emily Chen",
                          outcome: "Transferred",
                          duration: "1:58",
                          color: "amber",
                        },
                        {
                          time: "1:10 PM",
                          name: "Robert Wilson",
                          outcome: "Voicemail",
                          duration: "0:45",
                          color: "muted",
                        },
                      ].map((call, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.45 + i * 0.03 }}
                          className="flex items-center gap-2 md:gap-2 py-1.5 md:py-1.5 border-b border-dark-border/30 last:border-0"
                        >
                          <span className="text-[8px] md:text-[8px] text-dark-muted w-10 md:w-10">
                            {call.time}
                          </span>
                          <span className="text-[9px] md:text-[9px] font-medium text-dark-text flex-1 truncate">
                            {call.name}
                          </span>
                          <span
                            className={`text-[8px] md:text-[8px] w-16 md:w-16 truncate ${
                              call.color === "green"
                                ? "text-primary"
                                : call.color === "blue"
                                ? "text-blue-400"
                                : call.color === "amber"
                                ? "text-amber-400"
                                : "text-dark-muted"
                            }`}
                          >
                            {call.outcome}
                          </span>
                          <span className="text-[8px] md:text-[8px] text-dark-muted w-7 md:w-7 text-right">
                            {call.duration}
                          </span>
                          <button className="w-4 h-4 md:w-4 md:h-4 rounded-full bg-dark-muted/10 hover:bg-primary/20 flex items-center justify-center transition-colors group">
                            <Play className="w-2 h-2 md:w-2 md:h-2 text-dark-muted group-hover:text-primary" />
                          </button>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      );
    case "integrations":
      const integrations = [
        {
          name: "Google Calendar",
          logo: googleCalendarLogo,
          category: "Scheduling",
        },
        { name: "HubSpot", logo: hubspotLogo, category: "CRM" },
        { name: "Jobber", logo: jobberLogo, category: "Field Service" },
        { name: "OpenTable", logo: opentableLogo, category: "Reservations" },
        { name: "GoHighLevel", logo: ghlLogo, category: "Marketing" },
        {
          name: "Housecall Pro",
          logo: housecallProLogo,
          category: "Home Service",
        },
      ];

      return (
        <div className="relative w-full h-full flex items-center justify-center p-3 md:p-4">
          {/* Canvas background with subtle grid dots */}
          <div className="absolute inset-0 opacity-20">
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(circle, hsl(var(--dark-muted)) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 w-full max-w-[380px] sm:max-w-[420px] md:max-w-[520px] bg-dark-surface rounded-xl md:rounded-2xl border border-dark-border shadow-2xl overflow-hidden"
            style={{ boxShadow: "0 0 60px rgba(0,0,0,0.4)" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-3 md:px-5 py-3 md:py-4 border-b border-dark-border">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-7 h-7 md:w-9 md:h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                  <Plug className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-xs md:text-sm font-semibold text-dark-text">
                    Connected Integrations
                  </h3>
                  <p className="text-[9px] md:text-[11px] text-dark-muted">
                    All systems synced automatically
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 md:gap-1.5 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full bg-primary/10 border border-primary/20">
                <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-primary" />
                <span className="text-[9px] md:text-[10px] font-medium text-primary">
                  6 Active
                </span>
              </div>
            </div>

            {/* Integration Grid */}
            <div className="p-3 md:p-4">
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                {integrations.map((integration, i) => (
                  <motion.div
                    key={integration.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + i * 0.06, duration: 0.3 }}
                    className="flex items-center gap-2 md:gap-3 p-2 md:p-3 rounded-lg md:rounded-xl bg-[hsl(216_28%_9%)] border border-dark-border/50 hover:border-dark-border transition-colors"
                  >
                    {/* Logo */}
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-md md:rounded-lg bg-white flex items-center justify-center overflow-hidden p-1 md:p-1.5 flex-shrink-0">
                      <Image
                        src={integration.logo}
                        alt={integration.name}
                        width={40}
                        height={40}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] md:text-[12px] font-medium text-dark-text truncate">
                        {integration.name}
                      </div>
                      <div className="text-[8px] md:text-[10px] text-dark-muted">
                        {integration.category}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-primary" />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-3 md:px-5 py-2.5 md:py-3 border-t border-dark-border bg-[hsl(216_28%_7%)]">
              <div className="flex items-center justify-between">
                <span className="text-[9px] md:text-[11px] text-dark-muted">
                  One-click setup • No code required
                </span>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 text-[9px] md:text-[11px] font-medium text-dark-text bg-dark-muted/10 hover:bg-dark-muted/20 rounded-md md:rounded-lg transition-colors"
                >
                  <span>Browse All</span>
                  <ChevronRight className="w-2.5 h-2.5 md:w-3 md:h-3" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      );
    case "workflows":
      return (
        <div className="relative w-full h-full flex items-center justify-center overflow-visible">
          {/* Canvas background with subtle grid dots */}
          <div className="absolute inset-0 opacity-20">
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(circle, hsl(var(--dark-muted)) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            />
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="relative flex flex-col items-center"
          >
            {/* SVG Connectors */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ overflow: "visible" }}
            >
              {/* Main vertical line from Lead Entry to AI Call */}
              <motion.line
                x1="50%"
                y1="44"
                x2="50%"
                y2="70"
                stroke="hsl(var(--dark-border))"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              />
              {/* AI Call to Conversation */}
              <motion.line
                x1="50%"
                y1="114"
                x2="50%"
                y2="140"
                stroke="hsl(var(--dark-border))"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.4, duration: 0.3 }}
              />
              {/* Conversation to branch split */}
              <motion.line
                x1="50%"
                y1="220"
                x2="50%"
                y2="246"
                stroke="hsl(var(--dark-border))"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.6, duration: 0.3 }}
              />
              {/* Branch lines - left (False) */}
              <motion.path
                d="M 50% 246 Q 50% 266, 25% 266 L 25% 280"
                fill="none"
                stroke="hsl(var(--dark-muted))"
                strokeWidth="2"
                strokeOpacity="0.4"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.8, duration: 0.4 }}
              />
              {/* Branch lines - right (True) */}
              <motion.path
                d="M 50% 246 Q 50% 266, 75% 266 L 75% 280"
                fill="none"
                stroke="hsl(210 50% 60%)"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.8, duration: 0.4 }}
              />
              {/* True path to Appointment */}
              <motion.line
                x1="75%"
                y1="324"
                x2="75%"
                y2="350"
                stroke="hsl(210 50% 60%)"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 1.0, duration: 0.3 }}
              />
              {/* Appointment to final branch */}
              <motion.line
                x1="75%"
                y1="394"
                x2="75%"
                y2="416"
                stroke="hsl(var(--dark-border))"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 1.2, duration: 0.3 }}
              />
              {/* Final branch - Success (left of right column) */}
              <motion.path
                d="M 75% 416 Q 75% 432, 60% 432 L 60% 446"
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 1.4, duration: 0.4 }}
              />
              {/* Final branch - Declined (right) */}
              <motion.path
                d="M 75% 416 Q 75% 432, 90% 432 L 90% 446"
                fill="none"
                stroke="hsl(var(--dark-muted))"
                strokeWidth="2"
                strokeOpacity="0.5"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 1.4, duration: 0.4 }}
              />
            </svg>

            {/* Node 1: Lead Entry */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="relative z-10 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-dark-surface border border-dark-border shadow-lg"
            >
              <div className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-blue-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
              </div>
              <span className="text-sm font-medium text-dark-text">
                Lead Entry
              </span>
            </motion.div>

            {/* Connector dot */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 }}
              className="w-2 h-2 rounded-full bg-dark-border my-1 z-10"
            />

            {/* Node 2: AI Call */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="relative z-10 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-dark-surface border border-dark-border shadow-lg mt-2"
            >
              <div className="w-7 h-7 rounded-lg bg-purple-500/15 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-purple-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <span className="text-sm font-medium text-dark-text">
                AI Call
              </span>
              {/* AI Badge */}
              <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                <svg
                  className="w-3 h-3 text-purple-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                </svg>
              </div>
            </motion.div>

            {/* Connector dot */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5 }}
              className="w-2 h-2 rounded-full bg-dark-border my-1 z-10"
            />

            {/* Node 3: Active AI Conversation (Main/Featured) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="relative z-10 px-5 py-4 rounded-2xl bg-gradient-to-br from-dark-surface to-[hsl(210_16%_14%)] border border-dark-border shadow-2xl mt-2"
              style={{
                boxShadow:
                  "0 0 40px rgba(95, 143, 132, 0.1), 0 8px 32px rgba(0,0,0,0.4)",
              }}
            >
              <div className="flex items-center justify-between gap-6 mb-3">
                <span className="text-sm font-semibold text-dark-text">
                  Active AI Conversation
                </span>
                <motion.div
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/15 border border-primary/25"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span className="text-[10px] font-medium text-primary uppercase tracking-wide">
                    Live
                  </span>
                </motion.div>
              </div>
              {/* Audio Waveform */}
              <div className="flex items-center justify-center gap-0.5 h-8">
                {Array.from({ length: 32 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-1 rounded-full bg-gradient-to-t from-blue-500 to-purple-500"
                    animate={{
                      height: [
                        8,
                        4 + Math.sin(i * 0.5) * 16 + Math.random() * 8,
                        8,
                      ],
                    }}
                    transition={{
                      duration: 0.8 + Math.random() * 0.4,
                      repeat: Infinity,
                      repeatType: "reverse",
                      delay: i * 0.03,
                    }}
                  />
                ))}
              </div>
            </motion.div>

            {/* Connector dot */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.7 }}
              className="w-2 h-2 rounded-full bg-dark-border my-1 z-10"
            />

            {/* Branch: Contacted = False / True */}
            <div className="flex items-start gap-8 mt-2 relative z-10">
              {/* False Branch */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 0.5, x: 0 }}
                transition={{ delay: 0.9, duration: 0.4 }}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-dark-surface/50 border border-dark-border/50"
              >
                <div className="w-5 h-5 rounded-md bg-dark-muted/20 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-dark-muted"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                    />
                  </svg>
                </div>
                <span className="text-xs text-dark-muted">
                  Contacted = False
                </span>
              </motion.div>

              {/* True Branch */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9, duration: 0.4 }}
                className="flex flex-col items-center"
              >
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 shadow-lg"
                  style={{ boxShadow: "0 0 20px rgba(245, 158, 11, 0.1)" }}
                >
                  <div className="w-5 h-5 rounded-md bg-amber-500/20 flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-amber-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-amber-200">
                    Contacted = True
                  </span>
                </div>

                {/* Connector */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1.1 }}
                  className="w-2 h-2 rounded-full bg-dark-border my-1"
                />

                {/* Appointment Booking */}
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1, duration: 0.4 }}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-dark-surface border border-dark-border shadow-lg mt-1"
                >
                  <div className="w-5 h-5 rounded-md bg-purple-500/15 flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-purple-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-dark-text">
                    Appointment Booking
                  </span>
                </motion.div>

                {/* Connector */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1.3 }}
                  className="w-2 h-2 rounded-full bg-dark-border my-1"
                />

                {/* Final branch: Success / Declined */}
                <div className="flex items-start gap-4 mt-1">
                  {/* Success */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.5, duration: 0.4 }}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/15 border border-primary/30 shadow-lg"
                    style={{ boxShadow: "0 0 16px rgba(95, 143, 132, 0.2)" }}
                  >
                    <div className="w-5 h-5 rounded-md bg-primary/25 flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <span className="text-xs font-medium text-primary">
                      Success
                    </span>
                    <span className="text-sm">🎉</span>
                  </motion.div>

                  {/* Declined */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 0.6, scale: 1 }}
                    transition={{ delay: 1.5, duration: 0.4 }}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-dark-surface/50 border border-dark-border/50"
                  >
                    <div className="w-5 h-5 rounded-md bg-red-500/10 flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-red-400/60"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                    <span className="text-xs text-dark-muted">Declined</span>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      );
    case "omnichannel": {
      // Use CSS classes for mobile optimization instead of hook
      return (
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Canvas background with subtle grid dots */}
          <div className="absolute inset-0 opacity-20">
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(circle, hsl(var(--dark-muted)) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            />
          </div>
          {/* Ambient background glow */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                "radial-gradient(ellipse 80% 60% at 50% 50%, hsl(var(--primary) / 0.15) 0%, transparent 70%)",
            }}
          />

          {/* Floating channel signals - distributed around the visual */}
          {[
            {
              icon: Phone,
              label: "Voice",
              x: -180,
              y: -100,
              delay: 0,
              color: "blue",
            },
            {
              icon: MessageSquare,
              label: "SMS",
              x: 160,
              y: -80,
              delay: 0.15,
              color: "green",
            },
            {
              icon: Inbox,
              label: "Email",
              x: -150,
              y: 80,
              delay: 0.25,
              color: "amber",
            },
            {
              icon: MessageSquare,
              label: "Chat",
              x: 180,
              y: 60,
              delay: 0.35,
              color: "purple",
            },
            {
              icon: MessageSquare,
              label: "DMs",
              x: -60,
              y: -140,
              delay: 0.1,
              color: "pink",
            },
          ].map((channel, i) => (
            <motion.div
              key={channel.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: [0, 0.9, 0.7],
                scale: [0.8, 1, 0.95],
                x: [channel.x * 1.2, channel.x, channel.x * 0.3],
                y: [channel.y * 1.2, channel.y, channel.y * 0.3],
              }}
              transition={{
                duration: 4,
                delay: channel.delay,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
              }}
              className="absolute z-10 hidden md:block"
              style={{ left: "50%", top: "50%" }}
            >
              <div
                className={`
                px-4 py-3 rounded-2xl backdrop-blur-sm
                bg-dark-surface/80 border border-dark-border/50
                shadow-lg
              `}
                style={{
                  boxShadow:
                    channel.color === "blue"
                      ? "0 8px 32px rgba(59, 130, 246, 0.15)"
                      : channel.color === "green"
                      ? "0 8px 32px rgba(34, 197, 94, 0.15)"
                      : channel.color === "amber"
                      ? "0 8px 32px rgba(245, 158, 11, 0.15)"
                      : channel.color === "purple"
                      ? "0 8px 32px rgba(168, 85, 247, 0.15)"
                      : "0 8px 32px rgba(236, 72, 153, 0.15)",
                }}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className={`
                    w-8 h-8 rounded-lg flex items-center justify-center
                    ${
                      channel.color === "blue"
                        ? "bg-blue-500/15"
                        : channel.color === "green"
                        ? "bg-green-500/15"
                        : channel.color === "amber"
                        ? "bg-amber-500/15"
                        : channel.color === "purple"
                        ? "bg-purple-500/15"
                        : "bg-pink-500/15"
                    }
                  `}
                  >
                    <channel.icon
                      className={`w-4 h-4
                      ${
                        channel.color === "blue"
                          ? "text-blue-400"
                          : channel.color === "green"
                          ? "text-green-400"
                          : channel.color === "amber"
                          ? "text-amber-400"
                          : channel.color === "purple"
                          ? "text-purple-400"
                          : "text-pink-400"
                      }
                    `}
                    />
                  </div>
                  <span className="text-sm font-medium text-dark-text">
                    {channel.label}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Subtle particle trails converging - Reduced on mobile for performance */}
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={`particle-${i}`}
              className="absolute w-1.5 h-1.5 rounded-full bg-primary/30"
              style={{ left: "50%", top: "50%" }}
              initial={{
                opacity: 0,
                x: Math.cos((i * Math.PI) / 2) * 200,
                y: Math.sin((i * Math.PI) / 2) * 150,
              }}
              animate={{
                opacity: [0, 0.6, 0],
                x: [Math.cos((i * Math.PI) / 2) * 200, 0],
                y: [Math.sin((i * Math.PI) / 2) * 150, 0],
              }}
              transition={{
                duration: 3,
                delay: i * 0.4,
                repeat: Infinity,
                ease: "easeIn",
              }}
            />
          ))}

          {/* Central unified inbox - the gravitational center */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative z-20"
          >
            {/* Soft glow ring */}
            <div
              className="absolute -inset-8 rounded-3xl opacity-40"
              style={{
                background:
                  "radial-gradient(ellipse at center, hsl(var(--primary) / 0.2) 0%, transparent 70%)",
              }}
            />

            {/* Main inbox card */}
            <div
              className="relative bg-dark-surface rounded-xl md:rounded-2xl border border-dark-border w-[240px] sm:w-[260px] md:w-[280px]"
              style={{
                boxShadow:
                  "0 0 60px rgba(95, 143, 132, 0.1), 0 25px 50px rgba(0,0,0,0.4)",
              }}
            >
              {/* Header */}
              <div className="px-5 py-4 border-b border-dark-border bg-[hsl(216_28%_9%)] rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
                      <Inbox className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-dark-text">
                        Unified Inbox
                      </div>
                      <div className="text-[10px] text-dark-muted">
                        All channels • Real-time
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="w-2 h-2 rounded-full bg-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Message list */}
              <div className="p-3 space-y-2">
                {[
                  {
                    type: "voice",
                    name: "Sarah M.",
                    preview: "Voicemail received",
                    time: "Just now",
                    unread: true,
                  },
                  {
                    type: "sms",
                    name: "John D.",
                    preview: "Thanks for the reminder!",
                    time: "2m ago",
                    unread: true,
                  },
                  {
                    type: "email",
                    name: "Acme Corp",
                    preview: "Re: Partnership inquiry",
                    time: "5m ago",
                    unread: false,
                  },
                  {
                    type: "chat",
                    name: "Support Bot",
                    preview: "New lead captured",
                    time: "8m ago",
                    unread: false,
                  },
                ].map((message, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className={`
                      flex items-start gap-3 p-3 rounded-xl transition-colors cursor-pointer
                      ${
                        message.unread
                          ? "bg-primary/5 border border-primary/10"
                          : "bg-dark-muted/5 border border-transparent hover:bg-dark-muted/10"
                      }
                    `}
                  >
                    <div
                      className={`
                      w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                      ${
                        message.type === "voice"
                          ? "bg-blue-500/15"
                          : message.type === "sms"
                          ? "bg-green-500/15"
                          : message.type === "email"
                          ? "bg-amber-500/15"
                          : "bg-purple-500/15"
                      }
                    `}
                    >
                      {message.type === "voice" && (
                        <Phone className="w-3.5 h-3.5 text-blue-400" />
                      )}
                      {message.type === "sms" && (
                        <MessageSquare className="w-3.5 h-3.5 text-green-400" />
                      )}
                      {message.type === "email" && (
                        <Inbox className="w-3.5 h-3.5 text-amber-400" />
                      )}
                      {message.type === "chat" && (
                        <MessageSquare className="w-3.5 h-3.5 text-purple-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span
                          className={`text-xs font-medium truncate ${
                            message.unread
                              ? "text-dark-text"
                              : "text-dark-muted"
                          }`}
                        >
                          {message.name}
                        </span>
                        <span className="text-[10px] text-dark-muted flex-shrink-0">
                          {message.time}
                        </span>
                      </div>
                      <p
                        className={`text-[11px] truncate ${
                          message.unread
                            ? "text-dark-text/80"
                            : "text-dark-muted"
                        }`}
                      >
                        {message.preview}
                      </p>
                    </div>
                    {message.unread && (
                      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Footer status */}
              <div className="px-4 py-3 border-t border-dark-border bg-[hsl(216_28%_7%)] rounded-b-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-1">
                      {["blue", "green", "amber", "purple", "pink"].map(
                        (color, i) => (
                          <div
                            key={i}
                            className={`w-4 h-4 rounded-full border-2 border-[hsl(216_28%_7%)]
                            ${
                              color === "blue"
                                ? "bg-blue-500/40"
                                : color === "green"
                                ? "bg-green-500/40"
                                : color === "amber"
                                ? "bg-amber-500/40"
                                : color === "purple"
                                ? "bg-purple-500/40"
                                : "bg-pink-500/40"
                            }
                          `}
                          />
                        )
                      )}
                    </div>
                    <span className="text-[10px] text-dark-muted">
                      5 channels synced
                    </span>
                  </div>
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="text-[10px] text-primary font-medium"
                  >
                    Live
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      );
    }
    case "billing":
      return (
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Canvas background with subtle grid dots */}
          <div className="absolute inset-0 opacity-20">
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(circle, hsl(var(--dark-muted)) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            />
          </div>
          {/* Subtle ambient glow */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              background:
                "radial-gradient(ellipse 60% 50% at 50% 50%, hsl(var(--primary) / 0.15) 0%, transparent 70%)",
            }}
          />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Billing panel container */}
            <div
              className="bg-dark-surface rounded-xl md:rounded-2xl border border-dark-border w-[380px] sm:w-[400px] md:w-[400px]"
              style={{
                boxShadow:
                  "0 0 60px rgba(0,0,0,0.3), 0 25px 50px rgba(0,0,0,0.4)",
              }}
            >
              {/* Header */}
              <div className="px-5 py-4 border-b border-dark-border bg-[hsl(216_28%_9%)] rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-dark-text">
                        Client Billing
                      </div>
                      <div className="text-[10px] text-dark-muted">
                        Automated • No invoices
                      </div>
                    </div>
                  </div>
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    <span className="text-[10px] text-primary font-medium">
                      Running
                    </span>
                  </motion.div>
                </div>
              </div>

              {/* Tier cards */}
              <div className="p-4">
                <div className="flex gap-3">
                  {[
                    { name: "Basic", price: 99, calls: "500", active: false },
                    { name: "Pro", price: 249, calls: "2,000", active: true },
                    {
                      name: "Enterprise",
                      price: 499,
                      calls: "Unlimited",
                      active: false,
                    },
                  ].map((tier, i) => (
                    <motion.div
                      key={tier.name}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}
                      className={`
                        relative flex-1 rounded-xl p-4 transition-all duration-300 cursor-pointer
                        ${
                          tier.active
                            ? "bg-primary/10 border-2 border-primary/40"
                            : "bg-dark-muted/5 border border-dark-border/50 hover:border-dark-border"
                        }
                      `}
                    >
                      {/* Active indicator */}
                      {tier.active && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.5, type: "spring" }}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center"
                        >
                          <Check className="w-3 h-3 text-white" />
                        </motion.div>
                      )}

                      <div
                        className={`text-[11px] font-medium mb-2 ${
                          tier.active ? "text-primary" : "text-dark-muted"
                        }`}
                      >
                        {tier.name}
                      </div>
                      <div className="flex items-baseline gap-0.5 mb-1">
                        <span
                          className={`text-xl font-bold ${
                            tier.active ? "text-dark-text" : "text-dark-text/70"
                          }`}
                        >
                          ${tier.price}
                        </span>
                        <span className="text-[10px] text-dark-muted">/mo</span>
                      </div>
                      <div
                        className={`text-[10px] ${
                          tier.active ? "text-dark-muted" : "text-dark-muted/60"
                        }`}
                      >
                        {tier.calls} calls
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Automation status */}
              <div className="px-4 pb-4">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="rounded-xl bg-[hsl(216_28%_8%)] border border-dark-border/50 p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-medium text-dark-text">
                      Active Clients
                    </span>
                    <span className="text-[11px] text-dark-muted">
                      12 subscribed
                    </span>
                  </div>

                  {/* Client avatars with gentle pulse */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex -space-x-2">
                      {[...Array(5)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.7 + i * 0.05 }}
                          className="w-7 h-7 rounded-full bg-gradient-to-br from-dark-muted/30 to-dark-muted/10 border-2 border-[hsl(216_28%_8%)] flex items-center justify-center"
                        >
                          <span className="text-[9px] font-medium text-dark-muted">
                            {["JD", "SM", "AC", "RK", "LM"][i]}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                    <span className="text-[10px] text-dark-muted">+7 more</span>
                  </div>

                  {/* Revenue indicator */}
                  <div className="flex items-center justify-between pt-3 border-t border-dark-border/30">
                    <div className="flex items-center gap-2">
                      <motion.div
                        animate={{
                          boxShadow: [
                            "0 0 0 0 rgba(95, 143, 132, 0)",
                            "0 0 0 8px rgba(95, 143, 132, 0.1)",
                            "0 0 0 0 rgba(95, 143, 132, 0)",
                          ],
                        }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className="w-2 h-2 rounded-full bg-primary"
                      />
                      <span className="text-[10px] text-dark-muted">
                        Next billing cycle in 18 days
                      </span>
                    </div>
                    <div className="text-[11px] font-semibold text-primary">
                      $2,988/mo
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-dark-border bg-[hsl(216_28%_7%)] rounded-b-2xl">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-dark-muted">
                    Auto-invoicing • Stripe connected
                  </span>
                  <div className="flex items-center gap-1 text-[10px] text-dark-muted">
                    <Settings className="w-3 h-3" />
                    <span>Configure</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      );
    default:
      return null;
  }
};

interface DarkScrollSectionProps {
  preloaderComplete?: boolean;
}

const DarkScrollSection = ({ preloaderComplete = true }: DarkScrollSectionProps) => {
  const stickyContainerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const { scrollYProgress } = useScroll({
    target: stickyContainerRef,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (latest) => {
      const index = Math.min(
        Math.floor(latest * features.length),
        features.length - 1
      );
      setActiveIndex(index);
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  return (
    <section id="features" className="relative bg-black">
      {/* PART A: Intro - scrolls away normally */}
      <div className="min-h-[70vh] md:min-h-screen flex items-center justify-center px-6 md:px-6 pt-24 pb-16 md:py-0">
        <div className="text-center max-w-4xl">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={preloaderComplete ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-dark-text headline-display px-2"
          >
            Everything you need to run a{" "}
            <span className="ayra-accent">profitable</span> AI agency.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={preloaderComplete ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="mt-4 md:mt-6 text-base md:text-lg text-dark-muted body-text px-2"
          >
            Ayra replaces your entire tech stack with a single command center.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={preloaderComplete ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
            className="mt-8 md:mt-12 text-dark-muted animate-bounce text-sm md:text-base"
          >
            ↓ Scroll to explore
          </motion.div>
        </div>
      </div>

      {/* PART B: Sticky Scroll Feature Section - Desktop */}
      <div
        ref={stickyContainerRef}
        className="relative hidden md:block"
        style={{ height: `${features.length * 100}vh` }}
      >
        <div className="sticky top-0 h-screen flex items-center">
          <div className="container mx-auto max-w-7xl px-4 md:px-6">
            <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-center">
              {/* Left: Text content */}
              <div className="space-y-6 md:space-y-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <motion.p 
                      className="section-label text-primary mb-3 md:mb-4"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 }}
                    >
                      The Platform
                    </motion.p>
                    <motion.h3 
                      className="text-xl sm:text-2xl md:text-4xl lg:text-5xl text-dark-text headline-display whitespace-pre-line"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {features[activeIndex].title}
                    </motion.h3>
                    <motion.p 
                      className="mt-3 md:mt-4 text-sm md:text-lg text-dark-muted body-text whitespace-pre-line"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {features[activeIndex].subtitle}
                    </motion.p>
                  </motion.div>
                </AnimatePresence>

                {/* Progress list */}
                <div className="space-y-2 md:space-y-3 pt-4 md:pt-6 border-t border-dark-border">
                  {features.map((feature, i) => {
                    const Icon = feature.icon;
                    const shortTitles = [
                      "Connect your agent",
                      "White-label",
                      "Client dashboard",
                      "Integrations",
                      "Workflows",
                      "Omnichannel",
                      "Billing",
                    ];
                    return (
                      <motion.div
                        key={feature.id}
                        animate={{
                          opacity: i === activeIndex ? 1 : 0.4,
                        }}
                        className="flex items-center gap-2 md:gap-3"
                      >
                        <div
                          className={`w-6 h-6 md:w-8 md:h-8 rounded-lg flex items-center justify-center transition-colors ${
                            i === activeIndex
                              ? "bg-primary text-white"
                              : "bg-dark-surface text-dark-muted"
                          }`}
                        >
                          <Icon className="w-3 h-3 md:w-4 md:h-4" />
                        </div>
                        <motion.span
                          className={`text-xs md:text-sm transition-colors ${
                            i === activeIndex
                              ? "text-primary font-medium"
                              : "text-dark-muted"
                          }`}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: i * 0.05 }}
                        >
                          {shortTitles[i]}
                        </motion.span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Right: Visual */}
              <div className="relative h-[200px] sm:h-[250px] md:h-[350px] lg:h-[450px] flex items-end md:items-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4 }}
                    className="absolute inset-0 scale-[0.6] md:scale-100 origin-bottom md:origin-center"
                  >
                    <FeatureVisual featureId={features[activeIndex].id} />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Simple stacked layout without scroll effect */}
      <div className="md:hidden pt-20 pb-16 px-5 space-y-20">
        {features.map((feature, index) => {
          const Icon = feature.icon;

          return (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={preloaderComplete ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* Step indicator */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <span className="section-label text-primary">
                  {index + 1} of {features.length}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-xl sm:text-2xl text-dark-text headline">
                {feature.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-dark-muted body-text leading-relaxed max-w-prose">
                {feature.subtitle}
              </p>

              {/* Visual */}
              <div className="mt-8 w-full relative flex items-center justify-center">
                <motion.div
                  className={`w-full ${
                    feature.id === "dashboard"
                      ? "max-w-[420px] sm:max-w-md"
                      : feature.id === "connect" ||
                        feature.id === "integrations" ||
                        feature.id === "billing"
                      ? "max-w-[380px] sm:max-w-sm"
                      : "max-w-[340px] sm:max-w-xs"
                  } mx-auto`}
                  style={{
                    transform:
                      "perspective(1000px) rotateX(2deg) rotateY(-1deg) scale(0.8)",
                    transformStyle: "preserve-3d",
                  }}
                >
                  <FeatureVisual featureId={feature.id} />
                </motion.div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export default DarkScrollSection;
